/* ============================================================================
   ProfilePriorityRadar
   ----------------------------------------------------------------------------
   v1.1.4 b2: 6-axis SVG radar chart that visualises the trainer's priorities
   profile on the home spine. Pure SVG, zero chart libraries.

   Axes are derived from the same UserPriorities object that powers the rest
   of the app (deriveFeatureWeights, collectionFoci, leagueFoci). When the
   user has not completed the Nex companion questionnaire, the chart renders
   a flat ring with an empty-state CTA that opens the questionnaire sheet.
   ========================================================================= */
import { useMemo } from "react";
import { useSession } from "@/components/SessionProvider";
import { deriveFeatureWeights } from "@/lib/userPriorities";
import { ChevronRight } from "lucide-react";
import { HeroCard } from "@/components/ui/HeroCard";

interface Axis {
  key: string;
  label: string;
  value: number; // 0..1
}

function buildAxes(weights: ReturnType<typeof deriveFeatureWeights>, priorities: ReturnType<typeof useSession>["userPriorities"]): Axis[] {
  // Collection: scaled by how many collection foci the user picked.
  const collection = Math.min(1, priorities.collectionFoci.length / 4);

  // Events: lifted by trade frequency + multi-style players (events often
  // benefit traders / collectors equally). Casuals get a softer reading.
  const styles = [priorities.primaryStyle, priorities.secondaryStyle].filter(Boolean);
  const isCasual = styles.includes("casual");
  let events = 0.4;
  if (priorities.tradeFrequency === "daily") events += 0.25;
  else if (priorities.tradeFrequency === "weekly") events += 0.15;
  if (styles.length === 2) events += 0.1;
  if (isCasual) events -= 0.1;
  events = Math.max(0, Math.min(1, events));

  // Six axes, ordered clockwise from top. Trade / Battle map to connect /
  // coach so the dex stays consistent with the rest of the app's elevation
  // weights. "Strategy" reads off Coach as well but at a softer slope so the
  // shape varies when a user is multi-style.
  return [
    { key: "trade", label: "Trade", value: weights.connect },
    { key: "battle", label: "Battle", value: weights.coach },
    { key: "collection", label: "Collection", value: Math.max(weights.catch, collection) },
    { key: "events", label: "Events", value: events },
    { key: "friends", label: "Friends", value: weights.connect },
    { key: "strategy", label: "Strategy", value: Math.max(weights.coach * 0.85, weights.storage * 0.5) },
  ];
}

export function ProfilePriorityRadar() {
  const { userPriorities, openNexQuestionnaire } = useSession();
  const weights = useMemo(() => deriveFeatureWeights(userPriorities), [userPriorities]);
  const completed = userPriorities.questionnaireCompletedAt !== null;
  const axes = useMemo(
    () => (completed ? buildAxes(weights, userPriorities) : buildAxes(weights, userPriorities).map((a) => ({ ...a, value: 0 }))),
    [weights, userPriorities, completed],
  );

  // SVG geometry. The drawable chart is `size` square, but the viewBox is
  // widened by `pad` on every edge so vertex labels (the longest is
  // "COLLECTION") render in the margin instead of clipping at the bounds.
  //
  // PR-E8 fix 1: shrink the label gutter and inflate the chart inside the
  // viewBox so the rendered ring fills the card edge-to-edge instead of
  // floating at ~70% of card width with thick padding around it. Combined
  // with the HeroCard body padding override below (radar-surface gets
  // p-2 + tight footer) the card is also visually SHORTER — the old p-4
  // body + mt-2 footer wasted ~36px of vertical space framing a small chart.
  // PR-E10 fix 2: asymmetric padding. Horizontal vertex labels (COLLECTION,
  // PVP, RAIDS) need wider margins to avoid clipping; vertical labels are
  // shorter so vertical padding can be tighter, reclaiming dead space below
  // the chart that pushed the radar card unnecessarily tall.
  // PR-E11 fix 1: padY tightened further (20 → 8). The vertical labels (TRADE
  // top, EVENTS bottom) sit at radius+12 = 90 from chart centre. With cy=110
  // the label vertical extent is [20..200], comfortably inside the 220-square
  // chart box. padY=8 only reserves a thin margin for the text's ascender +
  // descender (fontSize 10) without bloating the SVG aspect ratio. This
  // collapses the empty bands above TRADE and below EVENTS that were padding
  // out the radar card to ~300px tall (chart graphics only occupy ~180px).
  const size = 220;
  const padX = 40;
  const padY = 8;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 78;
  const rings = 4;
  const N = axes.length;

  function angleAt(i: number) {
    // Start at -90deg so first axis sits at the top, then clockwise.
    return (-Math.PI / 2) + (i / N) * Math.PI * 2;
  }
  function pointAt(i: number, value: number) {
    const angle = angleAt(i);
    const r = radius * Math.max(0.02, value);
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r] as const;
  }
  function labelAt(i: number) {
    const angle = angleAt(i);
    // PR-E8 fix 1: tighten the label radius from radius+18 to radius+12 so
    // the labels track the smaller pad gutter (was 48, now 28). The text
    // still renders inside the viewBox margin without clipping.
    const r = radius + 12;
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r] as const;
  }
  // Anchor each label away from the chart based on its angular position so it
  // grows outward rather than overrunning an edge. A small epsilon keeps the
  // top/bottom vertices centered.
  function labelAnchor(i: number): "start" | "middle" | "end" {
    const dx = Math.cos(angleAt(i));
    if (dx > 0.05) return "start";
    if (dx < -0.05) return "end";
    return "middle";
  }
  function labelBaseline(i: number): "auto" | "middle" | "hanging" {
    const dy = Math.sin(angleAt(i));
    if (dy > 0.05) return "hanging";
    if (dy < -0.05) return "auto";
    return "middle";
  }

  const polygon = axes.map((a, i) => pointAt(i, a.value).join(",")).join(" ");

  return (
    <HeroCard
      glow="soft"
      glowColor="var(--accent-fill)"
      // PR-E8 fix 1: override HeroCard's default p-4 body padding so the
      // radar fills the card width-wise and the card itself is shorter
      // height-wise. `radar-surface-compact` collapses the body padding
      // to a thin 8px frame around the chart (see tokens.css).
      className="radar-surface radar-surface-compact"
      data-testid="profile-priority-radar"
    >
      {/* PR-E11 fix 2: eyebrow ("PRIORITIES RADAR") + Tune button on TOP again.
          PR-E5-radar moved them under the chart to make the radar feel like the
          hero element, but on-device that left the heading floating at the very
          bottom of the card visually separated from the radar it labels. Moving
          them back to the top reinforces the affordance pattern used by every
          other section header in the app and lets the chart fall naturally
          underneath. The chart still fills the card width-wise. */}
      <div className="flex items-center justify-between mb-1.5">
        <p
          className="text-[9px] font-bold uppercase tracking-[0.18em]"
          style={{ color: "hsl(var(--primary))" }}
        >
          Priorities radar
        </p>
        <button
          onClick={openNexQuestionnaire}
          data-testid="button-radar-edit"
          className="text-[10px] font-semibold inline-flex items-center gap-0.5 hover-elevate rounded px-1.5 py-0.5 text-[var(--brand-green-on-light)]"
        >
          {completed ? "Tune" : "Set up"}
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Radar chart sits BELOW the heading. The viewBox is padded by padX /
          padY (preserveAspectRatio="xMidYMid meet" is the SVG default) so
          vertex labels render in the padded margin and never clip. */}
      <div className="relative flex justify-center">
        <svg
          viewBox={`${-padX} ${-padY} ${size + padX * 2} ${size + padY * 2}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Trainer priorities radar chart"
        >
          {/* Rings */}
          {Array.from({ length: rings }).map((_, idx) => {
            const r = (radius * (idx + 1)) / rings;
            return (
              <circle
                key={`ring-${idx}`}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                className="radar-grid-stroke"
                strokeWidth={1}
              />
            );
          })}
          {/* Spokes */}
          {axes.map((_, i) => {
            const [lx, ly] = pointAt(i, 1);
            return (
              <line
                key={`spoke-${i}`}
                x1={cx}
                y1={cy}
                x2={lx}
                y2={ly}
                className="radar-spoke-stroke"
                strokeWidth={1}
              />
            );
          })}
          {/* Value polygon */}
          <polygon
            points={polygon}
            className="radar-polygon-fill"
            strokeWidth={1.5}
            style={{ filter: "drop-shadow(0 0 6px var(--accent-fill-glow-subtle))" }}
          />
          {/* Vertex dots */}
          {axes.map((a, i) => {
            const [px, py] = pointAt(i, a.value);
            return (
              <circle
                key={`v-${a.key}`}
                cx={px}
                cy={py}
                r={2.4}
                className="radar-vertex-fill"
              />
            );
          })}
          {/* Labels */}
          {axes.map((a, i) => {
            const [lx, ly] = labelAt(i);
            return (
              <text
                key={`l-${a.key}`}
                x={lx}
                y={ly}
                textAnchor={labelAnchor(i)}
                dominantBaseline={labelBaseline(i)}
                fontSize={10}
                fontWeight={700}
                className="radar-axis-label"
                style={{
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  // b17w: force the readable primary-text token so the axis
                  // labels never wash out against the card on either theme.
                  fill: "var(--text-primary)",
                }}
              >
                {a.label}
              </text>
            );
          })}
        </svg>

        {!completed && (
          <div
            className="nx-box-soft absolute inset-0 flex flex-col items-center justify-center text-center px-3"
            style={{ borderRadius: 14 }}
            data-testid="radar-empty-state"
          >
            <p className="text-ink-strong text-[12px] font-bold leading-tight">
              Complete your trainer questionnaire to see your profile
            </p>
            <button
              onClick={openNexQuestionnaire}
              data-testid="button-radar-cta"
              className="mt-2.5 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-extrabold"
              style={{
                background: "hsl(var(--primary))",
                color: "var(--text-on-accent)",
                boxShadow: "0 0 10px var(--accent-fill-glow)",
              }}
            >
              Take the questionnaire
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

    </HeroCard>
  );
}
